/**
 * Measurement metrics for every face this build can draw — loadable everywhere.
 *
 * Bundled faces come from the generated table below. Hosted faces are added by
 * `registerFaceMetrics` when `hostedFonts.ts` loads a pack, derived from the
 * BMFont JSON it fetched by the SAME function the generator used, so both
 * kinds of face measure by one rule.
 *
 * The Text behavior measures glyph advances through this registry rather
 * than through the fetched font atlas, so measurement is IDENTICAL in the
 * browser and headless (NullEngine — exports, bakes, vitest): the numbers
 * here are generated verbatim from the same BMFont JSONs the renderer
 * fetches (`npm run generate:font-metrics`), and a test pins the
 * equivalence. The atlas remains the source for *rendering* (glyph quads
 * need atlas coordinates); this table carries only what measurement needs
 * (advances, kerning, em size).
 *
 * Resolution walks the same fallback chain as the asset registry
 * (`resolveAgainst`), so the measured face is always the rendered face.
 */

import type { FontFamily, FontWeight, FontStyle } from '../../scenedata/behaviors/textdata';
import { resolveAgainst } from './fontResolve';
import { FONT_METRICS, FONT_METRICS_FIRST_CHAR, type GeneratedFaceMetrics } from './fontMetrics.generated';
import type { LayoutFontApi, LayoutFontChar } from './textlayout';

/** The id of the synthetic blank glyph a face answers for a character it does not have (the Unicode object replacement char). */
export const TOFU_CHAR_ID = 65532;

/** Measurement-relevant metrics for one bundled face. */
export interface FaceMetrics {
    /** Em-square size in font units (BMFont `info.size`). */
    emSize: number;
    /** Natural line height in font units (BMFont `common.lineHeight`). */
    commonLineHeight: number;
    /** Depth of the face's cap line below its line top, font units (`capTopFontUnits`). */
    capTop: number;
    /** Font API over the face's advance/kerning tables (measurement only). */
    fontApi: LayoutFontApi;
}

const METRICS_REGISTRY: Map<string, FaceMetrics> = (() => {
    const m = new Map<string, FaceMetrics>();
    for (const [key, face] of Object.entries(FONT_METRICS)) {
        m.set(key, buildFaceMetrics(face));
    }
    return m;
})();

/**
 * Install measurement metrics for a runtime-loaded face, derived from the
 * BMFont JSON `hostedFonts.ts` fetched — through `bmfontToMetrics`, the same
 * arithmetic the build-time generator ran over the bundled atlases, so a
 * hosted face and a bundled one cannot measure differently.
 *
 * Refuses a key the generator already emitted: those faces are bundled, their
 * numbers are pinned by tests, and nothing a deployment uploads may move
 * them. Returns `null` when it refused, otherwise a function that puts back
 * what was there before (nothing, or the cut this one replaced), for the caller
 * whose other half of the install fails.
 */
export function registerFaceMetrics(key: string, face: GeneratedFaceMetrics): (() => void) | null {
    if (FONT_METRICS[key] !== undefined) return null;
    const previous = METRICS_REGISTRY.get(key);
    METRICS_REGISTRY.set(key, buildFaceMetrics(face));
    return () => {
        if (previous) METRICS_REGISTRY.set(key, previous);
        else METRICS_REGISTRY.delete(key);
    };
}

/**
 * One number per kerning pair. Layout passes UTF-16 code units
 * (`charCodeAt`, always < 0x10000), so shifting `first` past 16 bits keeps
 * every pair distinct — Babylon keys a nested map, and a narrower multiplier
 * (e.g. × 1000) would alias (84, 8217) with (92, 217) once the atlases carry
 * codepoints above 999.
 */
function kerningKey(first: number, second: number): number {
    return first * 0x10000 + second;
}

function buildFaceMetrics(face: GeneratedFaceMetrics): FaceMetrics {
    const kernings = new Map<number, number>();
    for (let i = 0; i + 2 < face.kernings.length; i += 3) {
        kernings.set(kerningKey(face.kernings[i], face.kernings[i + 1]), face.kernings[i + 2]);
    }

    // Non-ASCII glyphs outside the dense table.
    const extraAdvances = new Map<number, number>();
    for (let i = 0; i + 1 < face.extraAdvances.length; i += 2) {
        extraAdvances.set(face.extraAdvances[i], face.extraAdvances[i + 1]);
    }

    const charCache = new Map<number, LayoutFontChar>();
    const emSize = face.emSize;

    // Mirrors Babylon FontAsset's `_getChar` fallback exactly: a char that
    // isn't in the atlas resolves to the synthetic TOFU glyph
    // (`xadvance = emSize × 0.5`, em-square ink, page −1), so table-measured
    // and atlas-measured text agree even on unsupported characters.
    const tofu: LayoutFontChar = {
        id: TOFU_CHAR_ID,
        width: emSize,
        height: emSize,
        xoffset: 0,
        yoffset: 0,
        xadvance: emSize * 0.5,
        page: -1,
    };

    const getChar = (charCode: number): LayoutFontChar => {
        const cached = charCache.get(charCode);
        if (cached) return cached;
        const idx = charCode - FONT_METRICS_FIRST_CHAR;
        const advance = idx >= 0 && idx < face.advances.length
            ? face.advances[idx]
            : extraAdvances.get(charCode) ?? -1;
        // Measurement needs only the horizontal advance; ink fields are
        // carried as zero so these synthetic chars never register as ink
        // (mirroring how measurement ignores them — the rendered ink comes
        // from the real atlas glyphs).
        const char: LayoutFontChar = advance >= 0
            ? { id: charCode, width: 0, height: 0, xoffset: 0, yoffset: 0, xadvance: advance, page: -1 }
            : tofu;
        charCache.set(charCode, char);
        return char;
    };

    return {
        emSize,
        commonLineHeight: face.commonLineHeight,
        capTop: face.capTop,
        fontApi: {
            getChar,
            getKerning: (first: number, second: number) => kernings.get(kerningKey(first, second)) ?? 0,
        },
    };
}

/**
 * Resolve the measurement metrics for a font triple, walking the same
 * fallback chain as `resolveFontAssets`. Always answers: the generator
 * emits an entry for every bundled face, and the default face is required
 * to be bundled.
 */
export function resolveFontMetrics(
    family: FontFamily,
    weight: FontWeight,
    style: FontStyle,
): FaceMetrics {
    return resolveAgainst(METRICS_REGISTRY, { family, weight, style });
}
